import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ClientPolicyDetail from './ClientPolicyDetail';
import { usePermissions } from '../context/PermissionsContext';

interface Policy {
  id: number;
  policy_number: string;
  product_name: string;
  insurer?: string;
  status: 'active' | 'cancelled';
  start_date: string;
  end_date?: string;
  renewal_date?: string;
  premium_amount: number;
  cancellation_date?: string;
  cancellation_reason?: string;
}

interface PoliciesTabProps {
  clientId: string;
}

const PoliciesTab = ({ clientId }: PoliciesTabProps) => {
  const { can } = usePermissions();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchPolicies();
  }, [clientId]);

  // Auto-open a policy if navigated to from elsewhere (e.g. Reports)
  useEffect(() => {
    const state = location.state as { policyId?: string } | null;
    if (state?.policyId && !loading && policies.length > 0) {
      const policyId = state.policyId;
      setSelectedPolicyId(policyId);
      setView('detail');
      // Clear state so refreshing doesn't re-open
      window.history.replaceState({}, document.title);
    }
  }, [location.state, loading, policies.length]);

    if (!can('policies', 'read')) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">You do not have permission to view Policies.</p>
      </div>
    );
  }

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/policies/client/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPolicies(response.data.data || response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch policies:', err);
      setError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyClick = (policyId: number) => {
    setSelectedPolicyId(policyId.toString());
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedPolicyId(null);
    fetchPolicies();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  const PolicyTable = ({ policies, title }: { policies: Policy[]; title: string }) => (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-3 text-gray-800">{title} ({policies.length})</h3>
      {policies.length === 0 ? (
        <p className="text-gray-500 italic">No {title.toLowerCase()} policies</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Policy #</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Insurer</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Start Date</th>
                {title !== 'Cancelled Policies' && (
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Renewal Date</th>
                )}
                {title !== 'Cancelled Policies' && (
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Status</th>
                )}
                {title === 'Cancelled Policies' && (
                  <>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Cancelled On</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600 border-b">Reason</th>
                  </>
                )}
                <th className="px-4 py-2 text-center text-sm font-medium text-gray-600 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((policy) => (
                <tr key={policy.id} className="hover:bg-gray-50 border-b border-gray-100">
                  <td className="px-4 py-2 text-sm">
                    <button
                      onClick={() => handlePolicyClick(policy.id)}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-left font-medium"
                    >
                      {policy.policy_number}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-800">{policy.insurer || 'N/A'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{formatDate(policy.start_date)}</td>
                  {title !== 'Cancelled Policies' && (
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {policy.renewal_date ? formatDate(policy.renewal_date) : ''}
                    </td>
                  )}
                  {title !== 'Cancelled Policies' && (
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        policy.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {policy.status.charAt(0).toUpperCase() + policy.status.slice(1)}
                      </span>
                    </td>
                  )}
                  {title === 'Cancelled Policies' && (
                    <>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatDate(policy.cancellation_date)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{policy.cancellation_reason || 'N/A'}</td>
                    </>
                  )}
                  <td className="px-4 py-2 text-sm text-center">
                    <button
                      onClick={() => handlePolicyClick(policy.id)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // Detail view
  if (view === 'detail' && selectedPolicyId) {
    return (
      <ClientPolicyDetail
        policyId={selectedPolicyId}
        onBack={handleBackToList}
      />
    );
  }

  // List view
  if (loading) {
    return <div className="text-center py-8">Loading policies...</div>;
  }

  const activePolicies = policies.filter(p => p.status === 'active');
  const cancelledPolicies = policies.filter(p => p.status === 'cancelled');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">Client Policies</h3>
        <button
          onClick={() => navigate(`/create-policy/${clientId}`)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          + Create Policy
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      <PolicyTable policies={activePolicies} title="Active Policies" />
      <PolicyTable policies={cancelledPolicies} title="Cancelled Policies" />
    </div>
  );
};

export default PoliciesTab;